import { useEffect, useMemo, useState } from "react";

export function useLocalStorageState<T>(key: string, initialValue: T) {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : initialValue;
    } catch {
      return initialValue;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Storage can be disabled inside some TV WebViews; the app remains usable.
    }
  }, [key, value]);

  return [value, setValue] as const;
}

export function useSpatialNavigation(enabled = true) {
  useEffect(() => {
    if (!enabled) return undefined;

    function getFocusable() {
      return Array.from(
        document.querySelectorAll<HTMLElement>(
          "[data-focusable]:not([disabled]), button:not([disabled]), a[href], input:not([disabled])"
        )
      ).filter((node) => {
        const rect = node.getBoundingClientRect();
        const style = window.getComputedStyle(node);
        return rect.width > 0 && rect.height > 0 && style.visibility !== "hidden" && style.display !== "none";
      });
    }

    function center(rect: DOMRect) {
      return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    }

    function move(direction: "ArrowLeft" | "ArrowRight" | "ArrowUp" | "ArrowDown") {
      const focusables = getFocusable();
      if (!focusables.length) return;
      const active = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      if (!active || !focusables.includes(active)) {
        focusables[0]?.focus();
        return;
      }

      const activeRect = active.getBoundingClientRect();
      const activeCenter = center(activeRect);
      const horizontal = direction === "ArrowLeft" || direction === "ArrowRight";
      const forward = direction === "ArrowRight" || direction === "ArrowDown";

      const best = focusables
        .filter((candidate) => candidate !== active)
        .map((candidate) => {
          const rect = candidate.getBoundingClientRect();
          const candidateCenter = center(rect);
          const primary = horizontal ? candidateCenter.x - activeCenter.x : candidateCenter.y - activeCenter.y;
          const secondary = horizontal
            ? Math.abs(candidateCenter.y - activeCenter.y)
            : Math.abs(candidateCenter.x - activeCenter.x);
          const alignedOverlap = horizontal
            ? Math.max(0, Math.min(activeRect.bottom, rect.bottom) - Math.max(activeRect.top, rect.top))
            : Math.max(0, Math.min(activeRect.right, rect.right) - Math.max(activeRect.left, rect.left));
          const isAhead = forward ? primary > 8 : primary < -8;
          if (!isAhead) return null;
          const score = Math.abs(primary) * 2.4 + secondary * 1.6 - alignedOverlap * 0.8;
          return { candidate, score };
        })
        .filter(Boolean)
        .sort((a, b) => a!.score - b!.score)[0];

      best?.candidate.focus();
      best?.candidate.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
    }

    function onKeyDown(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      const typing = target?.tagName === "INPUT" || target?.tagName === "TEXTAREA";
      if (typing && event.key !== "Escape") return;
      if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) {
        event.preventDefault();
        move(event.key as "ArrowLeft" | "ArrowRight" | "ArrowUp" | "ArrowDown");
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled]);
}

export function useRelativeDate(date: string) {
  return useMemo(() => {
    const published = new Date(date);
    if (Number.isNaN(published.getTime())) return "";
    return new Intl.DateTimeFormat("uk-UA", { day: "numeric", month: "short", year: "numeric" }).format(published);
  }, [date]);
}
